import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { finalize, forkJoin, of, Subject, switchMap, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { EExamKey } from '@gray/domain/enums/exam-key.enum';
import { IPlanWeek } from '@gray/domain/models/plan/plan-week.model';
import { IPlanWeekItem } from '@gray/domain/models/plan/plan-week-item.model';
import { CoachStudentPlanService } from '../../../core/services/coach-student-plan.service';
import { CoachStudentService } from '../../../core/services/coach-student.service';
import { CoachTaskService } from '../../../core/services/coach-task.service';
import { CoachPeriodService } from '../../../core/services/coach-period.service';
import { IStudentExamScore, IStudentOverview } from '../../../core/models/student-overview.model';

/**
 * The week in one page: how much of the plan is done, how the last exam moved, how the tasks stand
 * and how many meetings there have been — assembled from the same endpoints the other tabs use.
 *
 * The summary sentence is built here rather than fetched: no endpoint writes one, and inventing a
 * server-side report would only hide where the numbers came from.
 */
@Component({
  selector: 'coach-weekly-report-tab',
  templateUrl: './weekly-report-tab.component.html',
  styleUrl: './weekly-report-tab.component.scss',
})
export class WeeklyReportTabComponent implements OnChanges, OnDestroy {
  @Input() student?: IStudentOverview | null;
  @Input() examKey: EExamKey = EExamKey.TYT;
  @Input() examName = '';
  @Input() levelName?: string | null;
  @Input() levelKey?: string | null;

  /** Plan of the week in progress. */
  planWeek?: IPlanWeek;
  planTotal = 0;
  planCompleted = 0;

  lastScore: number | null = null;
  scoreDelta: number | null = null;

  taskTotal = 0;
  taskCompleted = 0;

  isLoading = false;

  destroyed$ = new Subject();

  constructor(
    private planService: CoachStudentPlanService,
    private studentService: CoachStudentService,
    private taskService: CoachTaskService,
    private periodService: CoachPeriodService,
    private toastrService: ToastrService,
    private translate: TranslateService,
  ) {
  }

  ngOnChanges(): void {
    if (this.student) {
      this.getReport();
    }
  }

  studentId(): number | null {
    return this.student?.id ?? null;
  }

  planPercent(): number {
    return this.planTotal ? Math.round((this.planCompleted / this.planTotal) * 100) : 0;
  }

  openTaskCount(): number {
    return Math.max(0, this.taskTotal - this.taskCompleted);
  }

  meetingsDone(): number {
    return this.student?.meetings?.done ?? 0;
  }

  meetingsNext(): number {
    return this.student?.meetings?.next ?? 0;
  }

  getReport() {
    const studentId = this.studentId();
    const periodId = this.periodService.selectedId;
    if (!studentId) {
      return;
    }

    this.isLoading = true;
    forkJoin({
      planItems: this.getPlanItems(studentId, periodId),
      scores: this.studentService.getStudentExamScores(studentId, `?examKey=${this.examKey}&limit=2`),
      tasks: this.taskService.getTasks(studentId, '?page=1&size=1'),
      completedTasks: this.taskService.getTasks(studentId, '?page=1&size=1&filter[isCompleted]=true'),
    })
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isLoading = false))
      .subscribe(r => {
        const items = (r.planItems ?? []) as IPlanWeekItem[];
        this.planTotal = items.length;
        this.planCompleted = items.filter(item => !!item.completedAt).length;

        const scores = (r.scores.data?.scores ?? []) as IStudentExamScore[];
        const last = scores[scores.length - 1];
        const previous = scores.length > 1 ? scores[scores.length - 2] : null;
        this.lastScore = last ? last.score : null;
        this.scoreDelta = (last && previous) ? Number((last.score - previous.score).toFixed(2)) : null;

        this.taskTotal = r.tasks.data?.meta?.total ?? 0;
        this.taskCompleted = r.completedTasks.data?.meta?.total ?? 0;
      });
  }

  /** The report is about the week in progress; without a plan there is simply nothing to report. */
  private getPlanItems(studentId: number, periodId: number | null) {
    if (!periodId) {
      return of([]);
    }

    return this.planService.getWeeks(studentId, periodId).pipe(
      switchMap(r => {
        const weeks = (r.data ?? []) as IPlanWeek[];
        this.planWeek = weeks.find(week => week.isActive) ?? weeks[weeks.length - 1];
        if (!this.planWeek) {
          return of([]);
        }

        return this.planService.getWeekItems(studentId, this.planWeek.id, periodId).pipe(
          switchMap(items => of((items.data?.items ?? items.data ?? []) as IPlanWeekItem[])),
        );
      }),
    );
  }

  /**
   * The sentences are built one by one and only when the number behind them exists — a report that
   * says "0 net artışı" for a student who never sat an exam is worse than a shorter report.
   */
  summaryLines(): string[] {
    const lines: string[] = [];
    const name = this.student?.name ?? '';

    if (this.planTotal) {
      lines.push(this.translate.instant('students.weekly_report.summary_plan', {
        name,
        week: this.planWeek?.weekNumber ?? '',
        completed: this.planCompleted,
        total: this.planTotal,
        percent: this.planPercent(),
      }));
    }

    if (this.lastScore !== null) {
      lines.push(this.scoreDelta === null
        ? this.translate.instant('students.weekly_report.summary_score_single', {
          exam: this.examName, score: this.lastScore,
        })
        : this.translate.instant('students.weekly_report.summary_score', {
          exam: this.examName,
          score: this.lastScore,
          delta: `${this.scoreDelta > 0 ? '+' : ''}${this.scoreDelta}`,
        }));
    }

    if (this.levelName) {
      lines.push(this.translate.instant('students.weekly_report.summary_level', {level: this.levelName}));
    }

    if (this.taskTotal) {
      lines.push(this.translate.instant('students.weekly_report.summary_tasks', {
        completed: this.taskCompleted,
        total: this.taskTotal,
        open: this.openTaskCount(),
      }));
    }

    if (this.meetingsDone()) {
      lines.push(this.translate.instant('students.weekly_report.summary_meetings', {
        done: this.meetingsDone(),
        next: this.meetingsNext(),
      }));
    }

    return lines;
  }

  copyReport() {
    const text = this.summaryLines().join(' ');
    navigator.clipboard.writeText(text).then(() => {
      this.toastrService.success(this.translate.instant('students.weekly_report.copied'));
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
